import { useQuery } from '@apollo/client';
import { CloudSync } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { DataSourcesOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NewButton } from '../components';
import { DataSourcesListDocument } from '../queries/list.generated';
import { Table } from './components';

export const DataSourcesList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const { orderBy } = useOrderByParams<DataSourcesOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(DataSourcesListDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, ...(orderBy && { orderBy }) },
    onCompleted: (data) => handleOutOfBounds(data.network?.dataSources.pageInfo),
  });

  const dataSources = data?.network?.dataSources.nodes ?? [];
  const totalCount = useTotalCount(data?.network?.dataSources.pageInfo.nodesCount);

  return (
    <>
      <Toolbar
        actions={<NewButton />}
        titleIcon={<CloudSync />}
        titleText={t('pages.data_sources.title')}
      />
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar placeholder={t('pages.data_sources.search_placeholder')} search={search} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Count
                  selectedCount={0}
                  totalCount={totalCount}
                  thing="common.pluralize.data_source_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table loading={loading} dataSources={dataSources} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
